/**
 * 디지털 선도학교 운영 보고서 가이드 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 디지털 선도학교, 선도학교 운영 보고서, AI 디지털 교과서, 에듀테크 선도학교
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import '../../styles/design-system/index.css';

const SundoGuidePage = () => {
  const navigate = useNavigate();

  const reportSections = [
    {
      title: '1. 학교 현황 및 추진 배경',
      items: [
        '학교 규모 (학급 수, 학생 수, 교원 수)',
        '디지털 인프라 현황 (기기, 네트워크, SW)',
        '선도학교 선정 배경 및 목표',
        '전년도 디지털 교육 추진 성과'
      ]
    },
    {
      title: '2. 추진 체계 및 조직',
      items: [
        '디지털 선도학교 운영위원회 구성',
        '담당자별 역할 분담표',
        '외부 협력 기관 (교육청, 에듀테크 기업)',
        '교원 연수 계획'
      ]
    },
    {
      title: '3. 세부 추진 계획',
      items: [
        'AI 디지털 교과서 활용 계획 (교과, 학년)',
        '에듀테크 활용 수업 계획 (월별)',
        '학생 디지털 리터러시 교육',
        '학부모 대상 안내 및 소통 계획'
      ]
    },
    {
      title: '4. 예산 편성 및 집행 계획',
      items: [
        '교부금 예산 배분 (기기, SW, 연수 등)',
        '분기별 집행 계획',
        '정산 및 증빙 관리 방안'
      ]
    },
    {
      title: '5. 성과 관리 및 평가',
      items: [
        '정량적 성과 지표 (수업 적용률, 활용 빈도)',
        '정성적 성과 지표 (만족도, 수업 변화)',
        '중간점검 및 최종평가 일정',
        '우수사례 발굴 및 공유 계획'
      ]
    },
    {
      title: '6. 기대 효과 및 확산 방안',
      items: [
        '학생 학습 효과 기대',
        '교사 수업 혁신 기대',
        '지역 내 확산 계획',
        '지속가능성 확보 방안'
      ]
    }
  ];

  const checklistItems = [
    { category: '인프라', items: ['무선 AP 설치 현황', '학생용 태블릿/노트북 확보', '충전 시설 구비', 'LMS 계정 발급'] },
    { category: '교육과정', items: ['AI 디지털 교과서 적용 과목 선정', '에듀테크 활용 수업 시수 배정', '디지털 리터러시 교육 시간 확보'] },
    { category: '교원 연수', items: ['에듀테크 활용 기초 연수', 'AI 디지털 교과서 사용법 연수', '수업 설계 워크숍 참여'] },
    { category: '학부모 소통', items: ['디지털 교육 안내 가정통신문', '학부모 설명회 개최', '우려사항 Q&A 자료 배포'] }
  ];

  return (
    <>
      <Helmet>
        <title>디지털 선도학교 운영 보고서 작성 가이드 | 양식·체크리스트 (2026)</title>
        <meta name="description" content="디지털 선도학교 운영 보고서 작성 가이드. AI 디지털 교과서 활용 계획, 에듀테크 추진 체계, 예산 편성, 성과 관리 항목별 체크리스트. 학교 담당자용 실무 가이드." />
        <meta name="keywords" content="디지털 선도학교, 선도학교 운영 보고서, AI 디지털 교과서, 에듀테크 선도학교, 선도학교 계획서, 디지털 교육 보고서, AI 교과서 활용" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/sundo" />
        
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/sundo" />
        <meta property="og:title" content="디지털 선도학교 운영 보고서 작성 가이드" />
        <meta property="og:description" content="AI 디지털 교과서 활용 계획, 체크리스트 포함." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:locale" content="ko_KR" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "디지털 선도학교 운영 보고서 작성하는 방법",
            "description": "디지털 선도학교 담당자를 위한 운영 보고서 작성 가이드",
            "step": reportSections.map((section, idx) => ({
              "@type": "HowToStep",
              "name": section.title,
              "text": section.items.join(', ')
            }))
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #dbeafe 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#bfdbfe',
              color: '#1e40af',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              2026 AI 디지털 교과서 시대
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              디지털 선도학교<br />
              <span style={{ color: '#2563eb' }}>운영 보고서 가이드</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              보고서 구성 항목 & 실무 체크리스트<br />
              <strong>담당자용 완벽 가이드</strong>
            </p>
          </div>
        </section>

        {/* 보고서 구성 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            운영 보고서 구성 항목
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {reportSections.map((section, idx) => (
              <div key={idx} style={{
                padding: '24px',
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                borderLeft: '4px solid #3b82f6'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '16px'
                }}>
                  {section.title}
                </h3>
                <ul style={{
                  margin: 0,
                  paddingLeft: '20px'
                }}>
                  {section.items.map((item, i) => (
                    <li key={i} style={{
                      fontSize: '14px',
                      color: '#374151',
                      lineHeight: '1.8'
                    }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* 체크리스트 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#f8fafc'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              선도학교 준비 체크리스트
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {checklistItems.map((cat, idx) => (
                <div key={idx} style={{
                  padding: '24px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2563eb',
                    marginBottom: '16px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #dbeafe'
                  }}>
                    {cat.category}
                  </h4>
                  <ul style={{
                    margin: 0,
                    padding: 0,
                    listStyle: 'none'
                  }}>
                    {cat.items.map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        fontSize: '14px',
                        color: '#374151',
                        lineHeight: '1.6',
                        marginBottom: '8px'
                      }}>
                        <span style={{ color: '#9ca3af' }}>☐</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 에듀집 연계 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            padding: '32px',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1e40af',
              marginBottom: '16px'
            }}>
              에듀집(Eduzip)과 연계하세요
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#1e40af',
              lineHeight: '1.7',
              marginBottom: '20px'
            }}>
              선도학교에서 사용할 에듀테크 도구는<br />
              <strong>에듀집에서 교육부 필수 기준 충족 여부를 확인</strong>할 수 있습니다.
            </p>
            <Link to="/guide/edzip" style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: '#fff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              에듀집 활용 가이드 보기 →
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              선도학교 보고서 문서,<br />
              <span style={{ color: '#3182f6' }}>AI가 자동 작성</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              학교 현황만 입력하면<br />
              <strong>보고서 초안 자동 생성</strong>
            </p>

            <button
              onClick={() => navigate('/chat')}
              style={{
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#3182f6',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              AI 채팅으로 물어보기
            </button>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/edzip" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              에듀집 활용 가이드
            </Link>
            <Link to="/guide/learning-software" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              학습지원 소프트웨어 기준
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default SundoGuidePage;
